import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaCode, FaMobile, FaGlobe, FaRocket } from 'react-icons/fa';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect mobile/tablet
  useEffect(() => {
    const checkMobile = () => {
      const wasMobile = isMobile;
      const nowMobile = window.innerWidth <= 1024;
      setIsMobile(nowMobile);
      // Close menu when switching from mobile to desktop
      if (wasMobile && !nowMobile && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile, isMenuOpen]);

  // Prevent body scroll when menu is open (mobile only)
  useEffect(() => {
    if (isMobile && isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen, isMobile]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  const handleNavClick = (href, e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    
    if (location.pathname !== '/') {
      // If not on home page, navigate to home first
      navigate('/');
      // Wait for navigation, then scroll
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // If on home page, just scroll
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const navItems = [
    { name: 'Home', href: '#home', isHome: true },
    { name: 'Services', href: '#services', isHome: false },
    { name: 'About', href: '#about', isHome: false },
    { name: 'Contact', href: '#contact', isHome: false }
  ];

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isMobile && isMenuOpen && (
        <motion.div
          className="menu-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <motion.header 
        className={`header ${scrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="container">
          <Link to="/" onClick={() => setIsMenuOpen(false)}>
            <motion.div 
              className="logo"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="logo-icon">
                <FaRocket />
              </div>
              <span className="logo-text">Infinity Logs</span>
            </motion.div>
          </Link>

          <motion.nav 
            className={`nav ${isMenuOpen ? 'nav-open' : ''}`}
            initial={false}
            animate={isMobile && isMenuOpen ? {
              x: 0,
              opacity: 1
            } : isMobile ? {
              x: '100%',
              opacity: 0
            } : {
              x: 0,
              opacity: 1
            }}
            transition={{ 
              duration: 0.3,
              ease: "easeInOut"
            }}
          >
            {navItems.map((item, index) => (
              <motion.div
                key={item.name}
                className="nav-item-wrapper"
                initial={false}
                animate={isMobile && isMenuOpen ? {
                  opacity: 1,
                  x: 0
                } : isMobile ? {
                  opacity: 0,
                  x: 20
                } : {
                  opacity: 1,
                  x: 0
                }}
                transition={{ 
                  delay: isMobile && isMenuOpen ? index * 0.1 : 0,
                  duration: 0.3
                }}
              >
                {item.isHome && location.pathname === '/' ? (
                  <a 
                    href={item.href} 
                    className="nav-link"
                    onClick={(e) => handleNavClick(item.href, e)}
                  >
                    {item.name}
                  </a>
                ) : item.isHome ? (
                  <Link 
                    to="/" 
                    className="nav-link"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a 
                    href={item.href} 
                    className="nav-link"
                    onClick={(e) => handleNavClick(item.href, e)}
                  >
                    {item.name}
                  </a>
                )}
              </motion.div>
            ))}
          </motion.nav>

          <motion.button
            className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileTap={{ scale: 0.9 }}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </motion.button>
        </div>

      {/* Floating tech icons */}
      <div className="floating-icons">
        <motion.div
          className="floating-icon"
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{ left: '10%', top: '20%' }}
        >
          <FaCode />
        </motion.div>
        <motion.div
          className="floating-icon"
          animate={{ 
            y: [0, -15, 0],
            rotate: [0, -5, 0]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          style={{ right: '15%', top: '30%' }}
        >
          <FaMobile />
        </motion.div>
        <motion.div
          className="floating-icon"
          animate={{ 
            y: [0, -25, 0],
            rotate: [0, 10, 0]
          }}
          transition={{ 
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          style={{ left: '80%', top: '60%' }}
        >
          <FaGlobe />
        </motion.div>
      </div>
      </motion.header>
    </>
  );
};

export default Header;
